import { useEffect } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { useUserContext } from '../context/use-user-context';
import { Featureflag } from '../constants/featureFlags';

const SCROLL_DEAD_ZONE = 10;
const CADDIE_ROOT_ID = 'caddie-root';

interface UseHideOnScrollOptions {
  enabled?: boolean;
}

/**
 * Hides the widget when the user scrolls down on mobile, shows it on scroll up.
 * Applies a CSS transform directly on the #caddie-root container for a clean,
 * full off-screen slide regardless of inner layout or margins.
 * Uses a dead zone to avoid flicker from elastic/momentum scrolling.
 * Only active on mobile viewports.
 *
 * IMPORTANT: The transform is always cleared on cleanup/disable. Any non-none
 * CSS transform creates a new containing block for position:fixed descendants
 * (CSS spec §9.3). Leaving translateY(0) on #caddie-root on desktop would
 * confine the exit-intent backdrop and centred widget overlay to the widget
 * container instead of the viewport.
 */
export const useHideOnScroll = ({
  enabled = false,
}: UseHideOnScrollOptions): void => {
  const { isFeatureEnabled } = useUserContext();
  const isFlagEnabled = isFeatureEnabled(Featureflag.HIDE_ON_SCROLL);
  const isMobile = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    const root = document.getElementById(CADDIE_ROOT_ID);
    if (!root) return;

    if (!enabled || !isFlagEnabled || !isMobile) {
      root.style.transform = '';
      return;
    }

    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY;

      if (Math.abs(delta) < SCROLL_DEAD_ZONE) return;

      if (delta > 0) {
        root.style.transition = 'transform 0.25s cubic-bezier(0.4, 0, 1, 1)';
        root.style.transform = 'translateY(calc(100% + 4rem))';
      } else {
        root.style.transition = 'transform 0.3s cubic-bezier(0, 0, 0.2, 1)';
        root.style.transform = 'translateY(0)';
      }

      lastScrollY = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      root.style.transform = '';
    };
  }, [enabled, isFlagEnabled, isMobile]);
};
